/**
 * BarChartControl
 *
 * Vertical or horizontal bar chart display
 * Supports stacking, grouping, and multiple series
 */

import React, { useMemo, memo } from 'react';
import type { ControlRendererProps } from '../ControlRenderer';
import {
  BaseChartComponent,
  type ChartDataPoint,
  type ChartSeriesConfig,
  extractChartData,
  getSeriesConfig,
} from './BaseChartComponent';

const BarChartControl: React.FC<ControlRendererProps> = memo(({ control, value }) => {
  // CRITICAL: ONLY use control.config.staticData - NO fallbacks to value prop
  const staticData = (control.config?.staticData as ChartDataPoint[] | undefined) || [];

  console.log('✅ BarChartControl USING config.staticData:', {
    controlId: control.id,
    label: control.label,
    dataLength: staticData.length,
    data: staticData,
  });

  // Use staticData ONLY - no fallback to value prop
  const chartData = useMemo(() => {
    return staticData; // ONLY use configured data
  }, [staticData]);

  const seriesConfig = useMemo(() => getSeriesConfig(control), [control]);

  // Get bar chart specific config
  const barConfig = useMemo(() => {
    const config = control.config as Record<string, unknown> | undefined;
    return {
      stacking: (config?.stacking as 'none' | 'positive' | 'percent') || 'none',
      layout: (config?.layout as 'vertical' | 'horizontal') || 'vertical',
      barCategoryGap: ((config?.barCategoryGap as number) || 10) as number,
    };
  }, [control.config]);

  // Render SVG-based bar chart
  const renderBars = useMemo(() => {
    if (!chartData || chartData.length === 0 || seriesConfig.length === 0) {
      return null;
    }

    // Calculate dimensions
    const padding = 40;
    const width = 600;
    const height = (control.config?.height as number) || 400;
    const plotWidth = width - 2 * padding;
    const plotHeight = height - 2 * padding;

    // Get X axis values (category axis)
    const xAxisKey = seriesConfig[0]?.dataKey?.split('.')[0] || 'name';
    const categories = chartData.map((d) => String((d as Record<string, unknown>)[xAxisKey] || ''));

    // Calculate scales
    const xScale = plotWidth / Math.max(categories.length, 1);
    const values = chartData.flatMap((d) =>
      seriesConfig.map((s) => {
        const val = (d as Record<string, unknown>)[s.dataKey];
        return typeof val === 'number' ? val : 0;
      }),
    );
    const maxValue = Math.max(...values, 1);
    const yScale = plotHeight / maxValue;

    const barWidth = (xScale * 0.6) / Math.max(seriesConfig.length, 1);

    return (
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: '100%' }}>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = height - padding - ratio * plotHeight;
          return (
            <line
              key={`grid-${ratio}`}
              x1={padding}
              y1={y}
              x2={width - padding}
              y2={y}
              stroke="#e5e7eb"
              strokeWidth="1"
            />
          );
        })}

        {/* Y axis labels */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const value = Math.round(ratio * maxValue);
          const y = height - padding - ratio * plotHeight;
          return (
            <text
              key={`y-label-${ratio}`}
              x={padding - 10}
              y={y}
              textAnchor="end"
              fontSize="12"
              fill="#6b7280"
            >
              {value}
            </text>
          );
        })}

        {/* Y axis */}
        <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#000" />

        {/* X axis */}
        <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#000" />

        {/* Bars */}
        {chartData.map((dataPoint, dataIndex) => {
          return seriesConfig.map((series, seriesIndex) => {
            const value = (dataPoint as Record<string, unknown>)[series.dataKey] as number | undefined;
            const numValue = typeof value === 'number' ? value : 0;

            const x = padding + dataIndex * xScale + seriesIndex * barWidth;
            const barHeight = (numValue / maxValue) * plotHeight;
            const y = height - padding - barHeight;

            return (
              <rect
                key={`bar-${dataIndex}-${seriesIndex}`}
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                fill={series.color || '#8884d8'}
                opacity="0.8"
              />
            );
          });
        })}

        {/* X axis labels */}
        {categories.map((category, index) => {
          const x = padding + index * xScale + xScale / 2;
          return (
            <text
              key={`x-label-${index}`}
              x={x}
              y={height - padding + 20}
              textAnchor="middle"
              fontSize="12"
              fill="#6b7280"
            >
              {category.substring(0, 10)}
            </text>
          );
        })}

        {/* Legend */}
        {(control.config?.legend as Record<string, unknown> | undefined)?.visible !== false && (
          <g>
            {seriesConfig.map((series, index) => {
              const legendX = width - padding - 150;
              const legendY = padding + index * 25;
              return (
                <g key={`legend-${series.id}`}>
                  <rect
                    x={legendX}
                    y={legendY}
                    width={16}
                    height={16}
                    fill={series.color || '#8884d8'}
                  />
                  <text x={legendX + 20} y={legendY + 13} fontSize="12" fill="#374151">
                    {series.name}
                  </text>
                </g>
              );
            })}
          </g>
        )}
      </svg>
    );
  }, [chartData, seriesConfig, control.config, barConfig]);

  return (
    <BaseChartComponent control={control} value={value} chartType="bar-chart">
      <div style={{ width: '100%', height: '100%' }}>{renderBars}</div>
    </BaseChartComponent>
  );
});

BarChartControl.displayName = 'BarChartControl';

export default BarChartControl;
